import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { api } from "../../lib/chalkApi";
import { ChalkCard, DarkHeader, SectionTitle } from "../../components/chalk/primitives";
import { toast } from "sonner";
import { RefundDialog } from "./RefundDialog";
import { RefundReasonsCard } from "./RefundReasonsCard";

export default function AdminBilling() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [reasons, setReasons] = useState(null);

  async function load() {
    try {
      const data = await api.adminBilling();
      setAccounts(data.accounts || []);
      setReasons(await api.adminRefundReasons(90));
    } catch (e) { toast.error(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function action(user_id, fn, label) {
    setBusy(user_id);
    try { await fn(user_id); toast.success(label); load(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  }

  async function refund(reason, note) {
    const a = refunding;
    setBusy(a.user_id);
    try {
      const r = await api.adminRefundAndCancel(a.user_id, reason, note);
      const amt = r.amount_cents != null ? `$${(r.amount_cents / 100).toFixed(2)} refunded` : "No refundable payment found";
      toast.success(`${amt}. Plan ${r.downgraded_now ? "ended" : "cancelled; it turns off in a few seconds"}.`);
      setRefunding(null);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  }

  return (
    <>
      <DarkHeader eyebrow="Admin" title="Billing controls" subtitle="Refunds go through Stripe. The other buttons change plans by hand." right={<BackButton onClick={() => navigate(-1)} />} />
      <div style={{ padding: "0 18px 24px" }}>
        <SectionTitle label="Lifecycle" />
        <ChalkCard data-testid="billing-sweep-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div>
              <div style={{ fontSize: 14, color: "var(--chalk-board)", fontWeight: 500 }}>Sweep expired trials</div>
              <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 4 }}>
                Auto-downgrade every trial whose end date has passed.
              </div>
            </div>
            <SmallBtn
              testId="billing-sweep-btn"
              onClick={async () => {
                try {
                  const r = await api.adminSweepTrials();
                  toast.success(`Swept ${r.swept} trial${r.swept === 1 ? "" : "s"}.`);
                  load();
                } catch (e) { toast.error(e.message); }
              }}
            >
              Run sweep
            </SmallBtn>
          </div>
        </ChalkCard>

        <SectionTitle label="Refunds" />
        <RefundReasonsCard data={reasons} />
        <RefundDialog key={refunding?.user_id || "none"} account={refunding} busy={!!refunding && busy === refunding.user_id}
                      onClose={() => setRefunding(null)} onConfirm={refund} />

        <SectionTitle label="Accounts" />
        {accounts.map((a) => (
          <ChalkCard key={a.user_id} data-testid={`billing-row-${a.user_id}`} style={{ marginTop: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--chalk-board)" }}>{a.email}</div>
                <div style={{ fontSize: 12, color: "var(--chalk-muted)" }}>
                  {a.role} · {a.subscription ? `${a.subscription.tier} · ${a.subscription.status}` : "no subscription"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <SmallBtn onClick={() => action(a.user_id, (id) => api.adminUpgrade(id, "chalkboard"), "Upgraded to Chalkboard.")} disabled={busy === a.user_id} testId={`upgrade-chalkboard-${a.user_id}`}>Chalkboard</SmallBtn>
                <SmallBtn onClick={() => action(a.user_id, (id) => api.adminUpgrade(id, "clipboard_plus"), "Upgraded to Clipboard+.")} disabled={busy === a.user_id}>Clipboard+</SmallBtn>
                <SmallBtn onClick={() => action(a.user_id, (id) => api.adminStartTrial(id), "Chalkboard trial started.")} disabled={busy === a.user_id} testId={`trial-chalkboard-${a.user_id}`}>Start trial</SmallBtn>
                <SmallBtn onClick={() => action(a.user_id, (id) => api.adminStartTrial(id, "clipboard_plus"), "Clipboard+ trial started.")} disabled={busy === a.user_id} testId={`trial-clipboard-plus-${a.user_id}`}>Clipboard+ trial</SmallBtn>
                <SmallBtn onClick={() => action(a.user_id, api.adminExpireTrial, "Trial expired.")} disabled={busy === a.user_id}>Expire trial</SmallBtn>
                {(!a.subscription || a.subscription.status !== "active") && (
                  <SmallBtn onClick={() => action(a.user_id, api.adminDemoRenew, "Renewed Chalkboard.")} disabled={busy === a.user_id} testId={`renew-${a.user_id}`}>Renew</SmallBtn>
                )}
                {a.subscription?.refundable && (
                  <SmallBtn onClick={() => setRefunding(a)} disabled={busy === a.user_id} danger testId={`refund-cancel-${a.user_id}`}>Refund and cancel</SmallBtn>
                )}
                <SmallBtn onClick={() => action(a.user_id, api.adminDowngrade, "Downgraded.")} disabled={busy === a.user_id} danger>Downgrade</SmallBtn>
              </div>
            </div>
          </ChalkCard>
        ))}
      </div>
    </>
  );
}

function SmallBtn({ children, onClick, disabled, danger, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        fontSize: 11, padding: "5px 10px", borderRadius: 999,
        background: danger ? "var(--chalk-red-bg)" : "#fff",
        color: danger ? "var(--chalk-red)" : "var(--chalk-board)",
        border: `0.5px solid ${danger ? "#F1C5C5" : "var(--chalk-border)"}`,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, fontWeight: 500,
      }}
    >
      {children}
    </button>
  );
}

export function BackButton({ onClick }) {
  return (
    <button onClick={onClick} aria-label="Back" data-testid="admin-back" style={{ background: "transparent", border: "0.5px solid rgba(245,240,232,0.20)", color: "var(--chalk-white)", borderRadius: 999, padding: "6px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}>
      <ArrowLeft size={14} /> Back
    </button>
  );
}
